// src/pages/ImportExportPage.jsx
//
// Browser front-end for the two management commands
// (import_projects/export_projects), hitting the new
// GET /tracker/import-export/export/ and POST /tracker/import-export/import/
// endpoints (tracker/import_export_views.py) instead of the CLI.
//
// Export is open to anyone signed in -- it's a read, matches the org
// dashboard's sensitivity level. Import is admin-only server-side
// (ImportProjectsView uses IsAdminRole), so the upload panel here is
// hidden for non-admins rather than shown-then-403'd.

import { useState } from "react";
import { apiFetchBlob, apiFetchForm } from "../api/client";
import Navbar from "../components/Navbar";
import { STAGE_TITLES } from "../constants";

function ExportSection() {
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState("");

  const handleDownload = async () => {
    setDownloading(true);
    setError("");
    try {
      const blob = await apiFetchBlob("/tracker/import-export/export/");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wh-project-tracker-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || "Couldn't export.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="card">
      <h3>Export</h3>
      <p className="stage-card-empty">
        Download every project, site, and fieldwork session as an Excel workbook -- the same
        three-sheet format the importer below expects.
      </p>
      <div className="form-actions">
        <button type="button" className="btn-primary" onClick={handleDownload} disabled={downloading}>
          {downloading ? "Preparing..." : "Download workbook"}
        </button>
      </div>
      {error && <p className="form-error">{error}</p>}
    </div>
  );
}

function ImportResult({ result }) {
  const { stats, warnings, errors, dry_run: dryRun } = result;
  return (
    <div className="import-result">
      {dryRun && <p className="import-result-dryrun">Dry run -- nothing was saved.</p>}
      <table className="fields-table">
        <tbody>
          <tr>
            <td className="fields-table-label">Projects</td>
            <td>
              {stats.projects_created} created, {stats.projects_updated} updated,{" "}
              {stats.projects_skipped} skipped
            </td>
          </tr>
          {Object.keys(STAGE_TITLES).map((key) => (
            <tr key={key}>
              <td className="fields-table-label">{STAGE_TITLES[key]}</td>
              <td>{stats[`${key}_written`] ?? 0} row(s) written</td>
            </tr>
          ))}
          <tr>
            <td className="fields-table-label">Sites</td>
            <td>{stats.sites_written} row(s) written</td>
          </tr>
          <tr>
            <td className="fields-table-label">Fieldwork sessions</td>
            <td>{stats.sessions_written} row(s) written</td>
          </tr>
        </tbody>
      </table>

      {warnings.length > 0 && (
        <>
          <p className="import-result-heading">{warnings.length} warning(s)</p>
          <ul className="import-result-list">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </>
      )}

      {errors.length > 0 && (
        <>
          <p className="import-result-heading import-result-heading-error">
            {errors.length} row(s) skipped due to errors
          </p>
          <ul className="import-result-list">
            {errors.map((e, i) => (
              <li key={i} className="form-error">
                {e}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

function ImportSection() {
  const [file, setFile] = useState(null);
  const [dryRun, setDryRun] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setSubmitting(true);
    setError("");
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const data = await apiFetchForm(
        `/tracker/import-export/import/?dry_run=${dryRun}`,
        formData
      );
      setResult(data);
    } catch (err) {
      setError(err.message || "Couldn't import.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="card">
      <h3>Import</h3>
      <p className="stage-card-empty">
        Upload a workbook in the same format the export above produces. A blank cell never
        overwrites an existing value, so re-importing a sheet with only a few changed rows is
        safe -- claims made through the app won't get stomped on.
      </p>
      <form onSubmit={handleSubmit} className="stacked-form">
        <div>
          <label>Workbook (.xlsx)</label>
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => setFile(e.target.files[0] || null)}
          />
        </div>
        <div className="import-dryrun-toggle">
          <label>
            <input
              type="checkbox"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
            />{" "}
            Dry run (preview only, don't save)
          </label>
        </div>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={!file || submitting}>
            {submitting ? "Working..." : dryRun ? "Preview import" : "Import"}
          </button>
        </div>
      </form>

      {result && <ImportResult result={result} />}
    </div>
  );
}

export default function ImportExportPage() {
  // Read fresh on mount, not at module load -- see StageCard.jsx for why.
  const isAdmin = localStorage.getItem("role") === "admin";

  return (
    <>
      <Navbar />
      <main className="page page-medium">
        <div className="page-header">
          <h1>Import / export</h1>
        </div>

        <ExportSection />

        {isAdmin ? (
          <ImportSection />
        ) : (
          <div className="card">
            <h3>Import</h3>
            <p className="stage-card-empty">Only admins can import a workbook.</p>
          </div>
        )}
      </main>
    </>
  );
}
